import {
  forwardRef,
  useId,
  useRef,
  type ComponentProps,
  type ForwardedRef,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react'

import { Input as InputAriaComponent, NumberField, type InputProps as InputAriaProps } from 'react-aria-components'
import { type Control, type FieldValues, type UseControllerProps } from 'react-hook-form'

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useIsMobile } from '@/hooks'
import { cn } from '@/infra'

interface IInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: ForwardedRef<HTMLInputElement>
}

type IInputFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = IInputProps &
  Omit<UseControllerProps<TFieldValues>, 'control'> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    label: string
    beforeContent?: React.ReactNode
    afterContent?: React.ReactNode
    errorSize?: 'sm' | 'md' | 'lg'
  }

type INumberInputFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = InputAriaProps &
  Omit<UseControllerProps<TFieldValues>, 'control'> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    label: string
    numberFieldProps?: ComponentProps<typeof NumberField>
    onValueChange?: (value: number) => void
    helperText?: string
  }

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> {
  className?: string
}

const handleNumericInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
  if (!/\d/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
    e.preventDefault()
  }
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref: ForwardedRef<HTMLInputElement>) => (
    <input
      {...props}
      onKeyDown={(event) => {
        if (props.type === 'number') handleNumericInputKeyDown(event)
        props.onKeyDown?.(event)
      }}
      type={props.type === 'number' ? 'text' : props.type}
      ref={ref}
      className={cn('w-full flex-1 outline-none disabled:cursor-not-allowed', className)}
      autoComplete="off"
    />
  ),
)

export interface InputWrapperProps {
  children: ReactNode
  className?: string
  invalid?: boolean
  disabled?: boolean
}

export function InputWrapper({ children, className, invalid, disabled }: Readonly<InputWrapperProps>) {
  return (
    <div
      className={cn(
        'relative flex h-10 w-full items-center gap-1 rounded-md px-3 py-1 text-base transition-colors',
        'border-input border',
        'file:text-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'focus-within:border-primary focus-within:outline-none',
        'bg-neutral-50 text-neutral-500',
        'has-[input:not(:placeholder-shown)]:bg-slate-100 has-[input:not(:placeholder-shown)]:text-neutral-800',
        'aria-invalid:has-[input:not(:placeholder-shown)]:bg-neutral-100',
        '[&_input]:placeholder-neutral-500',
        'aria-disabled:cursor-not-allowed aria-disabled:border-neutral-200 aria-disabled:bg-neutral-100 aria-disabled:text-neutral-500 aria-disabled:placeholder-neutral-500',
        'aria-disabled:border-neutral-300 aria-disabled:has-[input:not(:placeholder-shown)]:bg-neutral-100 aria-disabled:has-[input:not(:placeholder-shown)]:text-neutral-500',
        invalid && 'border-destructive focus-within:border-destructive',
        className,
      )}
      aria-invalid={invalid}
      aria-disabled={disabled}
    >
      {children}
    </div>
  )
}

function InputField<TFieldValues extends FieldValues, TContext = unknown, TTransformed = TFieldValues>({
  control,
  name,
  label,
  beforeContent = null,
  afterContent = null,
  errorSize = 'lg',
  ...props
}: IInputFieldProps<TFieldValues, TContext, TTransformed>) {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormItem>
          <FormLabel aria-disabled={props.disabled}>{label}</FormLabel>
          <FormControl>
            <InputWrapper invalid={!!fieldState.error} disabled={props.disabled} className={cn(props.className)}>
              {beforeContent}
              <Input {...field} onPaste={field.onChange} {...props} />
              {afterContent}
            </InputWrapper>
          </FormControl>
          <FormMessage size={errorSize} />
        </FormItem>
      )}
    />
  )
}

const NumberInputField = <TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>({
  control,
  label,
  numberFieldProps,
  onValueChange,
  helperText,
  ...props
}: INumberInputFieldProps<TFieldValues, TContext, TTransformedValues>) => {
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()

  return (
    <FormField
      name={props.name}
      control={control}
      render={({ field }) => {
        const getCents = () => Math.round((field.value ?? 0) * 100)
        const setCents = (c: number) => {
          const newVal = c / 100
          if (numberFieldProps?.maxValue != null && newVal > numberFieldProps?.maxValue) return
          field.onChange(newVal)
          onValueChange?.(newVal)
        }
        return (
          <FormItem>
            <FormLabel id={id} aria-disabled={props.disabled}>
              {label}
            </FormLabel>
            <NumberField
              {...field}
              defaultValue={0}
              value={field.value ?? 0}
              formatOptions={numberFieldProps?.formatOptions ?? { minimumFractionDigits: 2, maximumFractionDigits: 2 }}
              minValue={numberFieldProps?.minValue ?? 0}
              maxValue={numberFieldProps?.maxValue}
              isDisabled={props.disabled}
              onChange={(v) => {
                if (!isMobile) {
                  if (Number.isNaN(v)) {
                    field.onChange(0)
                    onValueChange?.(0)
                  } else {
                    field.onChange(v)
                    onValueChange?.(v)
                  }
                }
              }}
              aria-labelledby={id}
            >
              <FormControl>
                <InputAriaComponent
                  {...props}
                  ref={inputRef}
                  onFocus={() => {
                    const inp = inputRef.current
                    if (!isMobile) {
                      inp?.select()
                    }
                  }}
                  onMouseUp={(e) => {
                    if (isMobile) e.preventDefault()
                  }}
                  onKeyDown={(e) => {
                    if (!isMobile) return
                    if (/^\d$/.test(e.key)) {
                      e.preventDefault()
                      const digit = Number.parseInt(e.key, 10)
                      setCents(getCents() * 10 + digit)
                    } else if (e.key === 'Backspace') {
                      e.preventDefault()
                      setCents(Math.floor(getCents() / 10))
                    }
                  }}
                  onPaste={(e) => {
                    if (isMobile) e.preventDefault()
                  }}
                  data-testid="number-input"
                  className={cn(
                    'border-input file:text-foreground placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-ring/10 flex h-10 w-full rounded-md border px-3 py-1 pe-2 text-base transition-colors file:border-0 file:bg-transparent file:text-base file:font-medium placeholder-shown:bg-transparent focus-visible:ring-3 focus-visible:outline-none disabled:cursor-not-allowed',
                    'aria-invalid:border-destructive',
                    field.value === 0 || field.value
                      ? 'bg-slate-100 text-neutral-800 aria-invalid:bg-neutral-100'
                      : 'bg-transparent text-neutral-700',
                    'disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:opacity-100',
                    props.className,
                  )}
                  autoComplete="off"
                />
              </FormControl>
            </NumberField>

            <FormMessage helperText={helperText} />
          </FormItem>
        )
      }}
    />
  )
}

export { Input, InputField, NumberInputField }
